import React, { useCallback, useEffect, useRef, useState } from 'react'

import './categories-icons.scss'

import {
  CategoriesShell,
  CategoriesRow,
  CategoriesInner,
  CategoryPill,
  CategoryIcon,
  CategoriesScrollTrack,
  CategoriesScrollThumb,
} from './categories.styled'

const getScrollMetrics = (row) => {
  if (!row) {
    return { canScroll: false, thumbWidth: 0, thumbLeft: 0 }
  }

  const { scrollWidth, clientWidth, scrollLeft } = row
  const canScroll = scrollWidth > clientWidth

  if (!canScroll) {
    return { canScroll: false, thumbWidth: 0, thumbLeft: 0 }
  }

  const thumbWidth = Math.max((clientWidth / scrollWidth) * clientWidth, 32)
  const maxThumbLeft = clientWidth - thumbWidth
  const scrollRatio = scrollLeft / (scrollWidth - clientWidth)

  return {
    canScroll: true,
    thumbWidth,
    thumbLeft: scrollRatio * maxThumbLeft,
  }
}

export const Categories = ({ types = [], selectedType = 'all', onSelect }) => {
  const rowRef = useRef(null)
  const dragStateRef = useRef(null)
  const [scrollMetrics, setScrollMetrics] = useState(() => getScrollMetrics(null))
  const [isDraggingThumb, setIsDraggingThumb] = useState(false)

  const updateScrollMetrics = useCallback(() => {
    setScrollMetrics(getScrollMetrics(rowRef.current))
  }, [])

  const scrollFromThumbLeft = useCallback((thumbLeft) => {
    const row = rowRef.current
    if (!row) return

    const { scrollWidth, clientWidth } = row
    const maxScrollLeft = scrollWidth - clientWidth
    const thumbWidth = Math.max((clientWidth / scrollWidth) * clientWidth, 32)
    const maxThumbLeft = clientWidth - thumbWidth

    if (maxThumbLeft <= 0 || maxScrollLeft <= 0) return

    const nextScrollLeft = (thumbLeft / maxThumbLeft) * maxScrollLeft
    row.scrollLeft = nextScrollLeft
    updateScrollMetrics()
  }, [updateScrollMetrics])

  const handleThumbPointerDown = useCallback((event) => {
    event.preventDefault()

    const row = rowRef.current
    if (!row) return

    dragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startScrollLeft: row.scrollLeft,
    }

    setIsDraggingThumb(true)
    event.currentTarget.setPointerCapture(event.pointerId)
  }, [])

  const handleThumbPointerMove = useCallback((event) => {
    const dragState = dragStateRef.current
    if (!dragState || dragState.pointerId !== event.pointerId) return

    const row = rowRef.current
    if (!row) return

    const { scrollWidth, clientWidth } = row
    const maxScrollLeft = scrollWidth - clientWidth
    const thumbWidth = Math.max((clientWidth / scrollWidth) * clientWidth, 32)
    const maxThumbLeft = clientWidth - thumbWidth

    if (maxThumbLeft <= 0 || maxScrollLeft <= 0) return

    const startThumbLeft = (dragState.startScrollLeft / maxScrollLeft) * maxThumbLeft
    const deltaX = event.clientX - dragState.startX
    const nextThumbLeft = Math.max(0, Math.min(maxThumbLeft, startThumbLeft + deltaX))

    scrollFromThumbLeft(nextThumbLeft)
  }, [scrollFromThumbLeft])

  const handleThumbPointerEnd = useCallback((event) => {
    const dragState = dragStateRef.current
    if (!dragState || dragState.pointerId !== event.pointerId) return

    dragStateRef.current = null
    setIsDraggingThumb(false)

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }, [])

  useEffect(() => {
    updateScrollMetrics()

    const row = rowRef.current
    if (!row || typeof ResizeObserver === 'undefined') return undefined

    let frameId = 0

    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frameId)
      frameId = requestAnimationFrame(() => {
        requestAnimationFrame(updateScrollMetrics)
      })
    })

    observer.observe(row)

    return () => {
      observer.disconnect()
      cancelAnimationFrame(frameId)
    }
  }, [types, updateScrollMetrics])

  const handleWheel = useCallback((event) => {
    const row = rowRef.current
    if (!row) return

    const { scrollWidth, clientWidth, scrollLeft } = row
    if (scrollWidth <= clientWidth) return

    const delta = event.deltaY !== 0 ? event.deltaY : event.deltaX
    if (delta === 0) return

    const maxScrollLeft = scrollWidth - clientWidth
    const nextScrollLeft = Math.max(0, Math.min(maxScrollLeft, scrollLeft + delta))

    if (nextScrollLeft !== scrollLeft) {
      event.preventDefault()
      row.scrollLeft = nextScrollLeft
      updateScrollMetrics()
    }
  }, [updateScrollMetrics])

  const handleSelect = (type) => (event) => {
    event.preventDefault()
    onSelect?.(type)
  }

  return (
    <CategoriesShell>
      <CategoriesRow
        ref={rowRef}
        onWheel={handleWheel}
        onScroll={updateScrollMetrics}
        aria-label="Categorías de juegos"
      >
        <CategoriesInner>
          <CategoryPill
            type="button"
            $active={selectedType === 'all'}
            onClick={handleSelect('all')}
          >
            <CategoryIcon className="game-icon icon-casino-game-all icon" />
            Todos
          </CategoryPill>

          {types.map((type) => (
            <CategoryPill
              key={type.game_type}
              type="button"
              $active={type.game_type === selectedType}
              onClick={handleSelect(type)}
            >
              <CategoryIcon className={`game-icon icon-casino-${type.icon_class} icon`} />
              {type.game_type}
            </CategoryPill>
          ))}
        </CategoriesInner>
      </CategoriesRow>

      {scrollMetrics.canScroll ? (
        <CategoriesScrollTrack aria-hidden="true">
          <CategoriesScrollThumb
            $isDragging={isDraggingThumb}
            style={{
              width: `${scrollMetrics.thumbWidth}px`,
              transform: `translateX(${scrollMetrics.thumbLeft}px)`,
            }}
            onPointerDown={handleThumbPointerDown}
            onPointerMove={handleThumbPointerMove}
            onPointerUp={handleThumbPointerEnd}
            onPointerCancel={handleThumbPointerEnd}
          />
        </CategoriesScrollTrack>
      ) : null}
    </CategoriesShell>
  )
}
